import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { PlatformSiteAri } from '@atlassian/ari/platform';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import AgentStudioSkillsQuery, {
	type GraphIntegrationSkillSurface,
} from './ui/__generated__/AgentStudioSkillsQuery.graphql';
import type { AgentStudioSkillsProps, AgentStudioSkillsQueries } from './ui/AgentStudioSkills';

export type { AgentStudioSkillsCapability, AgentStudioSkillsProps } from './ui/AgentStudioSkills';

type AgentStudioSkillsEntrypointComponent = EntryPointComponent<
	AgentStudioSkillsQueries,
	Record<string, never>,
	Omit<AgentStudioSkillsProps, 'queryRef'>,
	Partial<Omit<AgentStudioSkillsProps, 'queryRef'>>
>;

export const agentStudioSkillsEntrypoint: EntryPoint<
	AgentStudioSkillsEntrypointComponent,
	{
		capabilities?: AgentStudioSkillsProps['capabilities'];
		cloudId: string;
		onSkillClick?: AgentStudioSkillsProps['onSkillClick'];
		onSkillDeleted?: AgentStudioSkillsProps['onSkillDeleted'];
		renderHeaderActions?: AgentStudioSkillsProps['renderHeaderActions'];
		surface?: GraphIntegrationSkillSurface;
	}
> = createEntryPoint({
	root: JSResourceForInteraction<AgentStudioSkillsEntrypointComponent>(
		/* webpackChunkName: "@atlaskit-internal_agent-studio-skills-entrypoint" */ './ui/AgentStudioSkills',
		{
			moduleId: 'agent-studio-skills-entrypoint-id',
			moduleName: 'agent-studio-skills-entrypoint',
		},
	),
	getPreloadProps: ({
		capabilities,
		cloudId,
		surface,
		onSkillClick,
		onSkillDeleted,
		renderHeaderActions,
	}: {
		capabilities?: AgentStudioSkillsProps['capabilities'];
		cloudId: string;
		onSkillClick?: AgentStudioSkillsProps['onSkillClick'];
		onSkillDeleted?: AgentStudioSkillsProps['onSkillDeleted'];
		renderHeaderActions?: AgentStudioSkillsProps['renderHeaderActions'];
		surface?: GraphIntegrationSkillSurface;
	}) => {
		const isCustomSkillMetadataEnabled =
			fg('studio_fe_enable_custom_skills_in_agents') || fg('studio_custom_skills_m1_frontend');

		return {
			queries: {
				skillsQuery: {
					parameters: AgentStudioSkillsQuery,
					variables: {
						cloudId,
						contextAri: PlatformSiteAri.create({ siteId: cloudId }).toString(),
						productListSurface: fg('studio_external_org_skills_allowlist')
							? (surface ?? 'STUDIO')
							: null,
						// The directory and product metadata use distinct GraphQL surface enums.
						skillListSurface: fg('enable-rovo-chat-skill-allowlist')
							? (surface ?? ('ROVO' as const))
							: 'ROVO_AGENTS',
						isForgeSkillsEnabled: fg('enable-forge-skills-for-studio'),
						isStudioCustomSkillsM1FrontendEnabled: isCustomSkillMetadataEnabled,
						relevantFor: ['PLATFORM_SKILL'] as const,
					},
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
				},
			},
			extraProps: {
				capabilities,
				cloudId,
				onSkillClick,
				onSkillDeleted,
				renderHeaderActions,
			},
		};
	},
});
